import { api } from '@/lib/axios';

// ── Link metadata (anonymous, facility-aware) ─────────────────────────────

export interface LegalLink {
  documentType: string;
  /** Paragraph-config code used as the i18n label key. */
  title: string;
  /** External URL, or null when content is served inline via documentTitle/content. */
  url: string | null;
  /** Raw document title from l_legal_document (tokens already replaced). */
  documentTitle: string | null;
  /** HTML document body from l_legal_document.description, or null when not set. */
  content: string | null;
}

export interface LegalLinksResponse {
  privacyPolicy: LegalLink | null;
  termsOfService: LegalLink | null;
  hipaaNotice: LegalLink | null;
}

// ── Full document (authenticated) ─────────────────────────────────────────

export interface LegalDocument {
  documentKey: number;
  documentType: string;
  title: string;
  content: string | null;
  version: string | null;
  lastUpdated: string | null;
  url: string | null;
}

export interface PendingTermsDocument {
  documentKey: number;
  documentType: string;
  title: string;
  content: string | null;
  version: string;
  lastUpdated: string | null;
}

export interface AcceptTermsPayload {
  documentKey: number;
  documentVersion: string;
  accepted: boolean;
  practiceCode?: string | null;
}

export interface AcceptTermsResponse {
  redirectTo?: string;
}

export const legalService = {
  /**
   * Returns which legal documents are configured for the current facility
   * (resolved from the X-Site-Group header). Each property is null when no
   * document of that type is configured. Anonymous — safe to call pre-login.
   */
  getLinks: () =>
    api.get<LegalLinksResponse>('/Legal/Links').then(r => r.data),

  /** Full document content for authenticated users. */
  getPrivacyPolicy: () =>
    api.get<LegalDocument>('/Legal/PrivacyPolicy').then(r => r.data),

  getTermsOfService: () =>
    api.get<LegalDocument>('/Legal/TermsOfService').then(r => r.data),

  getHipaaNotice: () =>
    api.get<LegalDocument>('/Legal/HipaaNotice').then(r => r.data),

  getPendingTerms: async (practiceCode?: string | null, setupToken?: string): Promise<PendingTermsDocument | null> => {
    const params = practiceCode ? { practiceCode } : undefined;
    const headers = setupToken ? { Authorization: `Bearer ${setupToken}` } : undefined;
    const response = await api.get<PendingTermsDocument>('/Legal/PendingTerms', { params, headers });
    return response.status === 204 ? null : response.data;
  },

  acceptTerms: async (payload: AcceptTermsPayload, setupToken?: string): Promise<AcceptTermsResponse | null> => {
    const headers = setupToken ? { Authorization: `Bearer ${setupToken}` } : undefined;
    const response = await api.post<AcceptTermsResponse>('/Legal/AcceptTerms', payload, { headers });
    return response.status === 204 ? null : response.data;
  },
};
